import { useCallback, useRef } from "react";
import type { TimelineProject, TimelineAction, TimelineTextOverlay } from "@scroll/shared-types/timeline";
import { MIN_CLIP_DURATION } from "@scroll/shared-types/timeline";
import { getTotalDuration } from "@scroll/shared-types/timeline-utils";

interface TextOverlayTrackProps {
  project: TimelineProject;
  pixelsPerSecond: number;
  selectedOverlayId: string | null;
  onSelectOverlay: (id: string | null) => void;
  onDispatch: (action: TimelineAction) => void;
}

export default function TextOverlayTrack({
  project,
  pixelsPerSecond,
  selectedOverlayId,
  onSelectOverlay,
  onDispatch,
}: TextOverlayTrackProps) {
  if (project.textOverlays.length === 0) return null;

  const totalDuration = getTotalDuration(project);

  return (
    <div className="timeline-overlay-track">
      <span className="timeline-track-label">Text</span>
      {project.textOverlays.map((overlay) => (
        <OverlayBar
          key={overlay.id}
          overlay={overlay}
          pixelsPerSecond={pixelsPerSecond}
          totalDuration={totalDuration}
          isSelected={overlay.id === selectedOverlayId}
          onSelect={() => onSelectOverlay(overlay.id)}
          onDispatch={onDispatch}
        />
      ))}
    </div>
  );
}

function OverlayBar({
  overlay,
  pixelsPerSecond,
  totalDuration,
  isSelected,
  onSelect,
  onDispatch,
}: {
  overlay: TimelineTextOverlay;
  pixelsPerSecond: number;
  totalDuration: number;
  isSelected: boolean;
  onSelect: () => void;
  onDispatch: (action: TimelineAction) => void;
}) {
  const draggingRef = useRef<"left" | "right" | null>(null);
  const startXRef = useRef(0);
  const startTimesRef = useRef({ startTime: 0, endTime: 0 });
  const lastPatchRef = useRef<Partial<typeof overlay>>({});

  const left = overlay.startTime * pixelsPerSecond;
  const width = (overlay.endTime - overlay.startTime) * pixelsPerSecond;

  const handleHandleMouseDown = useCallback(
    (side: "left" | "right", e: React.MouseEvent) => {
      e.stopPropagation();
      draggingRef.current = side;
      startXRef.current = e.clientX;
      startTimesRef.current = {
        startTime: overlay.startTime,
        endTime: overlay.endTime,
      };
      lastPatchRef.current = {};

      const handleMouseMove = (e: MouseEvent) => {
        const dx = e.clientX - startXRef.current;
        const dt = dx / pixelsPerSecond;

        if (draggingRef.current === "left") {
          const newStart = Math.max(
            0,
            Math.min(
              startTimesRef.current.startTime + dt,
              startTimesRef.current.endTime - MIN_CLIP_DURATION,
            ),
          );
          lastPatchRef.current = { startTime: newStart };
          onDispatch({
            type: "UPDATE_TEXT_OVERLAY_PREVIEW",
            id: overlay.id,
            patch: { startTime: newStart },
          });
        } else {
          const newEnd = Math.min(
            totalDuration,
            Math.max(
              startTimesRef.current.endTime + dt,
              startTimesRef.current.startTime + MIN_CLIP_DURATION,
            ),
          );
          lastPatchRef.current = { endTime: newEnd };
          onDispatch({
            type: "UPDATE_TEXT_OVERLAY_PREVIEW",
            id: overlay.id,
            patch: { endTime: newEnd },
          });
        }
      };

      const handleMouseUp = () => {
        draggingRef.current = null;
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);

        const patch = lastPatchRef.current;
        if (Object.keys(patch).length > 0) {
          // Revert preview, then apply with undo history
          onDispatch({
            type: "UPDATE_TEXT_OVERLAY_PREVIEW",
            id: overlay.id,
            patch: {
              startTime: startTimesRef.current.startTime,
              endTime: startTimesRef.current.endTime,
            },
          });
          onDispatch({
            type: "UPDATE_TEXT_OVERLAY",
            id: overlay.id,
            patch,
          });
        }
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [overlay, pixelsPerSecond, totalDuration, onDispatch],
  );

  return (
    <div
      className={`timeline-overlay-bar ${isSelected ? "selected" : ""}`}
      style={{ left: `${left}px`, width: `${width}px` }}
      onClick={onSelect}
    >
      <span>{overlay.text}</span>
      <div
        className="timeline-overlay-bar-handle timeline-overlay-bar-handle-left"
        onMouseDown={(e) => handleHandleMouseDown("left", e)}
      />
      <div
        className="timeline-overlay-bar-handle timeline-overlay-bar-handle-right"
        onMouseDown={(e) => handleHandleMouseDown("right", e)}
      />
    </div>
  );
}
